import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Select, MenuItem, Box, Dialog, DialogContent,
  FormControl, InputLabel, Slider, Typography, Alert
} from '@mui/material';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { overallFeedback } from '../shared/dropdown_values';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import styled, { keyframes } from 'styled-components';
import { ThemeProvider } from '@mui/material/styles';
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, PAGE_PADDING, CONTROL_HEIGHT,
} from '../../styles/recruiterTheme';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// Standalone route (/feedback): fills the viewport with the lavender canvas and adds PAGE_PADDING.
// Embedded (rendered inside a dashboard's padded container): transparent, no outer padding.
const PageWrap = styled.div`
  font-family: ${FONT_BODY};
  color: ${COLORS.ink};
  box-sizing: border-box;
  * , *::before, *::after { box-sizing: border-box; }
  background: ${p => (p.$embedded ? 'transparent' : COLORS.background)};
  min-height: ${p => (p.$embedded ? 'auto' : '100vh')};
  padding: ${p => (p.$embedded ? 0 : `${PAGE_PADDING}px`)};
  animation: ${fadeUp} 0.4s ease both;
`;

// Back button and card share one column so their left edges line up.
const Column = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: ${CONTROL_HEIGHT.md}px;
  background: #fff;
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.sm}px;
  padding: 0 16px 0 12px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 600;
  color: ${COLORS.primaryDark};
  cursor: pointer;
  margin-bottom: 24px;
  box-shadow: ${SHADOW.card};
  transition: all 0.18s;
  &:hover { background: ${COLORS.background}; border-color: ${COLORS.borderStrong}; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const WizardCard = styled.div`
  background: #fff;
  border-radius: ${RADIUS.lg}px;
  border: 1px solid ${COLORS.border};
  box-shadow: ${SHADOW.card};
  overflow: hidden;
`;

const WizardHeader = styled.div`
  background: linear-gradient(135deg, #2D1B5E 0%, #4F378B 50%, #6750A4 100%);
  padding: 24px 32px 0;
  position: relative;
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 200px; height: 200px;
    border-radius: 50%;
    background: rgba(255,255,255,0.05);
  }
`;

const WizardTitle = styled.h1`
  font-family: ${FONT_DISPLAY};
  font-size: ${TYPE.section}px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px;
  letter-spacing: -0.02em;
`;

const WizardSub = styled.p`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  color: rgba(255,255,255,0.7);
  font-weight: 500;
  margin: 0 0 24px;
`;

const Stepper = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StepWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

const StepCircle = styled.div`
  width: 32px; height: 32px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px; font-weight: 800;
  font-variant-numeric: tabular-nums;
  transition: all 0.3s;
  background: ${p => p.$done ? 'rgba(255,255,255,0.9)' : p.$active ? '#fff' : 'rgba(255,255,255,0.1)'};
  color: ${p => p.$done || p.$active ? '#4F378B' : 'rgba(255,255,255,0.4)'};
  box-shadow: ${p => p.$active ? '0 0 0 4px rgba(255,255,255,0.2)' : 'none'};
`;

const StepLabel = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px; font-weight: 600;
  margin-top: 8px; padding-bottom: 16px;
  color: ${p => p.$active ? '#fff' : p.$done ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.38)'};
  white-space: nowrap;
`;

const StepLine = styled.div`
  flex: 1; height: 2px;
  margin-top: 16px;
  background: ${p => p.$done ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.15)'};
  transition: background 0.4s;
`;

const ProgressBar = styled.div`
  height: 3px;
  background: rgba(255,255,255,0.15);
  margin: 0 32px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: rgba(255,255,255,0.65);
  width: ${p => p.$pct}%;
  transition: width 0.45s ease;
`;

const WizardBody = styled.div`
  padding: 32px;
  animation: ${slideIn} 0.3s ease both;
`;

const SectionLabel = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px; font-weight: 700;
  color: ${COLORS.muted};
  text-transform: uppercase; letter-spacing: 0.06em;
  margin-bottom: 16px;
  display: flex; align-items: center; gap: 8px;
  &::after { content: ''; flex: 1; height: 1px; background: ${COLORS.border}; }
`;

// Candidate / JD / interview summary: label-value grid inside one tinted panel.
const InfoGrid = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
  margin: 0 0 8px;
  padding: 16px;
  background: ${COLORS.surfaceTint};
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.md}px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const InfoCard = styled.div`
  min-width: 0;
`;

const InfoLabel = styled.dt`
  font-size: ${TYPE.overline}px; font-weight: 700;
  color: ${COLORS.muted};
  text-transform: uppercase; letter-spacing: 0.06em;
  margin: 0 0 4px;
`;

const InfoValue = styled.dd`
  margin: 0;
  font-size: ${TYPE.body}px; font-weight: 600; color: ${COLORS.primaryDeep};
  overflow-wrap: anywhere;
`;

const EmailInput = styled.input`
  width: 100%; height: ${CONTROL_HEIGHT.lg}px;
  border: 1px solid ${COLORS.borderStrong}; border-radius: ${RADIUS.sm}px;
  padding: 0 16px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px; color: ${COLORS.ink};
  background: #fff; outline: none;
  margin-bottom: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus { border-color: ${COLORS.primary}; box-shadow: 0 0 0 1px ${COLORS.primary}; }
  &::placeholder { color: ${COLORS.faint}; }
`;

// Rating rows: fixed label column, slider, score badge — every row aligns on the same grid.
const SkillBlock = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr 64px;
  align-items: center;
  gap: 24px;
  min-height: 48px;
  margin-bottom: 8px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr 64px;
    gap: 8px 16px;
    & > .MuiSlider-root { grid-column: 1 / -1; grid-row: 2; }
  }
`;

const SkillName = styled.span`
  font-size: ${TYPE.body}px; font-weight: 600; color: ${COLORS.primaryDeep};
`;

const SkillBadge = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px; font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${p => p.$v >= 8 ? '#2E7D32' : p.$v >= 5 ? '#6750A4' : p.$v >= 3 ? '#E65100' : '#B3261E'};
  background: ${p => p.$v >= 8 ? '#E6F4EA' : p.$v >= 5 ? '#EDE7F6' : p.$v >= 3 ? '#FFF3E0' : '#FDECEA'};
  border-radius: ${RADIUS.sm}px;
  height: 32px; line-height: 32px;
  text-align: center;
  white-space: nowrap;
`;

const sliderSx = {
  color: '#6750A4', height: 6,
  '& .MuiSlider-thumb': {
    backgroundColor: '#fff', border: '3px solid #6750A4',
    width: 20, height: 20,
    '&:hover': { boxShadow: '0 0 0 8px rgba(103,80,164,0.12)' },
  },
  '& .MuiSlider-track': { backgroundColor: '#6750A4', border: 'none' },
  '& .MuiSlider-rail': { backgroundColor: '#EDE7F6' },
  '& .MuiSlider-mark': { backgroundColor: '#C4B0E8', width: 2, height: 2 },
  '& .MuiSlider-valueLabel': {
    backgroundColor: '#4F378B', borderRadius: `${RADIUS.sm}px`,
    fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.caption,
  },
};

const ScoreGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
  margin-bottom: 16px;
  @media (max-width: 600px) { grid-template-columns: repeat(2, 1fr); }
`;

const ScoreBox = styled.div`
  background: ${p => p.$bg}; border: 1px solid ${p => p.$bdr};
  border-radius: ${RADIUS.md}px; padding: 16px 8px; text-align: center;
`;

const ScoreNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.section}px; font-weight: 800; color: ${p => p.$clr}; line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const ScoreLbl = styled.div`
  font-size: ${TYPE.overline}px; font-weight: 700; color: ${COLORS.muted};
  text-transform: uppercase; letter-spacing: 0.06em; margin-top: 8px;
`;

const AvgBand = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 16px;
  background: linear-gradient(135deg, #F8F4FF, #EDE7F6);
  border-radius: ${RADIUS.md}px; padding: 16px 24px;
  border: 1px solid #E0D4F5; margin-bottom: 16px;
`;

const FeedbackTextarea = styled.textarea`
  display: block;
  width: 100%; min-height: 128px;
  border: 1px solid ${COLORS.borderStrong}; border-radius: ${RADIUS.sm}px;
  padding: 12px 16px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px; line-height: 1.5; color: ${COLORS.ink};
  background: #fff; resize: vertical; outline: none;
  margin: 0;
  transition: border-color 0.2s, box-shadow 0.2s;
  &::placeholder { color: ${COLORS.faint}; }
  &:focus { border-color: ${COLORS.primary}; box-shadow: 0 0 0 1px ${COLORS.primary}; }
`;

const StyledFormControl = styled(FormControl)`
  && {
    margin-bottom: 24px !important; width: 100%;
    .MuiOutlinedInput-root {
      height: ${CONTROL_HEIGHT.lg}px;
      border-radius: ${RADIUS.sm}px;
      font-family: ${FONT_BODY}; font-size: ${TYPE.body}px;
      background: #fff;
      &:hover fieldset { border-color: ${COLORS.primaryLight}; }
      &.Mui-focused fieldset { border-color: ${COLORS.primary}; border-width: 2px; }
    }
    .MuiInputLabel-root {
      font-family: ${FONT_BODY}; font-size: ${TYPE.body}px;
      transform: translate(14px, 10px) scale(1);
      &.MuiInputLabel-shrink { transform: translate(14px, -9px) scale(0.75); }
      &.Mui-focused { color: ${COLORS.primary}; }
    }
  }
`;

const ReviewSection = styled.div`
  background: ${COLORS.surfaceTint}; border-radius: ${RADIUS.md}px;
  border: 1px solid ${COLORS.border}; padding: 16px 24px; margin-bottom: 16px;
`;

const ReviewTitle = styled.div`
  font-family: ${FONT_BODY}; font-size: ${TYPE.overline}px; font-weight: 700;
  color: ${COLORS.muted}; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px;
`;

const ReviewRow = styled.div`
  display: grid; grid-template-columns: 160px 1fr; gap: 16px; align-items: baseline;
  padding: 8px 0; border-bottom: 1px solid ${COLORS.border};
  &:last-child { border-bottom: none; }
`;

const ReviewKey = styled.span`
  font-size: ${TYPE.small}px; font-weight: 500; color: ${COLORS.muted};
`;

const ReviewVal = styled.span`
  font-size: ${TYPE.small}px; font-weight: 600; color: ${COLORS.primaryDeep};
  text-align: right; overflow-wrap: anywhere;
`;

const BtnRow = styled.div`
  display: flex; justify-content: flex-end; align-items: center; gap: 12px;
  margin-top: 24px; padding-top: 24px; border-top: 1px solid ${COLORS.border};
`;

const BtnBack = styled.button`
  height: ${CONTROL_HEIGHT.md}px; border: 1px solid ${COLORS.borderStrong}; border-radius: ${RADIUS.sm}px;
  background: #fff; color: ${COLORS.primary};
  font-family: ${FONT_BODY}; font-weight: 600; font-size: ${TYPE.small}px;
  cursor: pointer; padding: 0 16px;
  display: inline-flex; align-items: center; gap: 8px;
  transition: all 0.18s;
  &:hover { background: ${COLORS.background}; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const BtnNext = styled.button`
  height: ${CONTROL_HEIGHT.md}px; border: none; border-radius: ${RADIUS.sm}px;
  background: linear-gradient(135deg, #6750A4, #4F378B);
  color: #fff;
  font-family: ${FONT_BODY}; font-weight: 600; font-size: ${TYPE.small}px;
  cursor: pointer; padding: 0 20px;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  box-shadow: 0 2px 8px rgba(79,55,139,0.24);
  transition: all 0.2s;
  &:hover { box-shadow: 0 4px 14px rgba(79,55,139,0.34); }
  &:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
`;

const getClr = v => v >= 8 ? '#2E7D32' : v >= 5 ? '#6750A4' : v >= 3 ? '#E65100' : '#B3261E';
const getBg  = v => v >= 8 ? '#E6F4EA' : v >= 5 ? '#EDE7F6' : v >= 3 ? '#FFF3E0' : '#FDECEA';
const getBdr = v => v >= 8 ? '#C8E6C9' : v >= 5 ? '#D8C8F5' : v >= 3 ? '#FFCC80' : '#FFCDD2';

const STEPS = ['Details', 'Ratings', 'Review'];

const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FEEDBACK_MIN = 20;
const FEEDBACK_MAX = 2000;
const VALID_DECISIONS = overallFeedback.map(o => o.value);
const SUCCESS_REDIRECT_MS = 2500;

// jdId may arrive as a raw id string or a populated jddetails object
const normalizeJdId = v => (v && typeof v === 'object' ? v._id || '' : v || '');

const isValidRating = v => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 10;

const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('rbotUser') || 'null') || {}; }
  catch { return {}; }
};

const FeedbackForm = ({
  setActiveTab, selectedCandidateEmail, selectedInterviewId,
  selectedJdId, onCloseFeedbackForm, recruiterEmail,
  selectedJdID, selectedJobRole, selectedCandidateID, selectedCandidateName,
  interviewStatus, onSubmitted, onBack,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location?.search || '');
  const state = location?.state || {};
  const pick = (prop, ...keys) => {
    if (prop) return prop;
    for (const k of keys) {
      if (state?.[k]) return state[k];
      if (params.get(k)) return params.get(k);
    }
    return '';
  };

  // Resolved inputs: props first, then location.state, then query string (route /feedback)
  const resolved = {
    interviewId:    pick(selectedInterviewId, 'selectedInterviewId', 'interviewId'),
    candidateEmail: pick(selectedCandidateEmail, 'candidateEmail', 'selectedCandidateEmail'),
    jdId:           normalizeJdId(pick(normalizeJdId(selectedJdId), 'selectedJdId', 'jdId')),
    jdID:           pick(selectedJdID, 'selectedJdID', 'jdID'),
    jobRole:        pick(selectedJobRole, 'selectedJobRole', 'jobRole'),
    candidateID:    pick(selectedCandidateID, 'selectedCandidateID', 'candidateID'),
    candidateName:  pick(selectedCandidateName, 'selectedCandidateName', 'candidateName'),
    recruiterEmail: pick(recruiterEmail, 'recruiterEmail'),
    interviewStatus: pick(interviewStatus, 'interviewStatus'),
  };
  const formKey = `${resolved.interviewId}|${resolved.candidateID}|${resolved.jdId}`;

  const [step, setStep] = useState(0);
  const [candidateEmail, setCandidateEmail] = useState(resolved.candidateEmail);
  const [feedback, setFeedback]         = useState('');
  const [reviewRating, setReviewRating] = useState('');
  const [loading, setLoading]           = useState(false);
  const [success, setSuccess]           = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [error, setError]               = useState('');
  const [programmingRating, setProgrammingRating]     = useState(null);
  const [technicalSkills, setTechnicalSkills]         = useState(null);
  const [communicationSkills, setCommunicationSkills] = useState(null);
  const [problemSolvingSkills, setProblemSolvingSkills] = useState(null);
  const inFlightRef = useRef(false);
  const timerRef = useRef(null);

  const { interviewId, jdId, jdID, jobRole, candidateID, candidateName } = resolved;

  const feedbackApiUrl = process.env.REACT_APP_FEEDBACK_API || 'http://127.0.0.1:3004/feedback';

  // Reset all form state when the target candidate/interview changes (no leaking between candidates)
  useEffect(() => {
    setStep(0);
    setCandidateEmail(resolved.candidateEmail);
    setFeedback('');
    setReviewRating('');
    setProgrammingRating(null);
    setTechnicalSkills(null);
    setCommunicationSkills(null);
    setProblemSolvingSkills(null);
    setError('');
    setSuccess(false);
    setSubmitted(false);
    inFlightRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleClose = useCallback(() => {
    clearTimeout(timerRef.current);
    if (typeof onCloseFeedbackForm === 'function') onCloseFeedbackForm();
    else if (typeof onBack === 'function') onBack();
    else if (window.history.length > 1) navigate(-1);
    else {
      const u = getStoredUser();
      navigate(String(u?.userType || '').toLowerCase() === 'interviewer' ? '/interviewer-dashboard' : '/');
    }
  }, [onCloseFeedbackForm, onBack, navigate]);

  const finishAfterSuccess = useCallback(() => {
    clearTimeout(timerRef.current);
    setSuccess(false);
    if (typeof setActiveTab === 'function') setActiveTab('InterviewerDashboard');
    sessionStorage.setItem('activeTab', 'InterviewerDashboard');
    handleClose();
  }, [setActiveTab, handleClose]);

  // Rendered inside a dashboard (it passes a close/back handler) vs the standalone /feedback route
  const embedded = typeof onCloseFeedbackForm === 'function' || typeof onBack === 'function';

  const missing = [];
  if (!OBJECT_ID_RE.test(interviewId || '')) missing.push('Interview ID');
  if (!OBJECT_ID_RE.test(jdId || '')) missing.push('JD');
  if (!candidateID) missing.push('Candidate ID');
  const alreadyCompleted = String(resolved.interviewStatus || '').toLowerCase() === 'completed';

  const skills = [
    { label: 'Programming Skills',     value: programmingRating,    set: setProgrammingRating },
    { label: 'Technical Skills',       value: technicalSkills,      set: setTechnicalSkills },
    { label: 'Communication Skills',   value: communicationSkills,  set: setCommunicationSkills },
    { label: 'Problem Solving Skills', value: problemSolvingSkills, set: setProblemSolvingSkills },
  ];

  const infoItems = [
    { label: 'JD ID',          value: jdID },
    { label: 'Job Role',       value: jobRole },
    { label: 'Interview ID',   value: interviewId },
    { label: 'Candidate ID',   value: candidateID },
    { label: 'Candidate Name', value: candidateName },
    { label: 'Email',          value: candidateEmail },
  ];

  const allRated = skills.every(sk => isValidRating(sk.value));
  const avg = allRated ? skills.reduce((s, sk) => s + sk.value, 0) / skills.length : 0;
  const pct = step === 0 ? 12 : step === 1 ? 55 : 92;
  const feedbackLen = feedback.trim().length;
  const emailValid = EMAIL_RE.test((candidateEmail || '').trim());

  const validateStep = s => {
    if (s === 0) {
      if (!emailValid) return 'Enter a valid candidate email.';
    }
    if (s >= 1) {
      if (!allRated) return 'Rate every skill (0 – 10) before continuing.';
      if (!VALID_DECISIONS.includes(reviewRating)) return 'Select an overall performance decision.';
      if (feedbackLen < FEEDBACK_MIN) return `Written feedback must be at least ${FEEDBACK_MIN} characters.`;
      if (feedbackLen > FEEDBACK_MAX) return `Written feedback must be at most ${FEEDBACK_MAX} characters.`;
    }
    return '';
  };

  const handleNext = () => {
    const msg = validateStep(step);
    if (msg) { setError(msg); return; }
    setError('');
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    if (inFlightRef.current || submitted || alreadyCompleted) return;
    if (missing.length) { setError(`Missing details: ${missing.join(', ')}.`); return; }
    const msg = validateStep(0) || validateStep(1);
    if (msg) { setError(msg); return; }

    inFlightRef.current = true;
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(feedbackApiUrl, {
        jdId, candidateEmail: candidateEmail.trim(), candidateID, interview_id: interviewId,
        feedback: feedback.trim(), reviewRating, programmingRating,
        technicalSkills, communicationSkills, problemSolvingSkills,
        jdID, recruiterEmail: resolved.recruiterEmail, candidateName,
      });
      if (res?.data?.error) throw new Error(res.data.error);

      setSubmitted(true);
      setSuccess(true);
      if (typeof onSubmitted === 'function') {
        try { onSubmitted({ interviewId, candidateID, reviewRating }); } catch (e) { console.error(e); }
      }
      timerRef.current = setTimeout(finishAfterSuccess, SUCCESS_REDIRECT_MS);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error || err?.response?.data?.message || err?.message ||
        'Failed to submit feedback. Please try again.'
      );
      inFlightRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  if (missing.length || alreadyCompleted) {
    return (
      <ThemeProvider theme={recruiterTheme}>
        <PageWrap $embedded={embedded}>
         <Column>
          <BackBtn type="button" onClick={handleClose}>
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
            Back
          </BackBtn>
          <WizardCard>
            <WizardBody>
              <SectionLabel>{alreadyCompleted ? 'Feedback Already Submitted' : 'Missing Details'}</SectionLabel>
              <Alert severity={alreadyCompleted ? 'info' : 'warning'} sx={{ borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5 }}>
                {alreadyCompleted
                  ? 'Feedback for this interview has already been recorded.'
                  : `This feedback form was opened without the required details (${missing.join(', ')}). Please go back and open it from the interview list.`}
              </Alert>
              <BtnRow>
                <BtnNext type="button" onClick={handleClose}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
                  Go Back
                </BtnNext>
              </BtnRow>
            </WizardBody>
          </WizardCard>
         </Column>
        </PageWrap>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={recruiterTheme}>
      <PageWrap $embedded={embedded}>
       <Column>
        <BackBtn type="button" onClick={handleClose} disabled={loading}>
          <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          Back
        </BackBtn>

        <WizardCard>
          {/* Header */}
          <WizardHeader>
            <WizardTitle>Candidate Evaluation</WizardTitle>
            <WizardSub>Complete all steps to submit your feedback</WizardSub>

            <Stepper>
              {STEPS.map((lbl, i) => (
                <React.Fragment key={i}>
                  <StepWrap>
                    <StepCircle $done={i < step} $active={i === step}>
                      {i < step ? <CheckIcon sx={{ fontSize: 14 }} /> : i + 1}
                    </StepCircle>
                    <StepLabel $done={i < step} $active={i === step}>{lbl}</StepLabel>
                  </StepWrap>
                  {i < STEPS.length - 1 && <StepLine $done={i < step} />}
                </React.Fragment>
              ))}
            </Stepper>

            <ProgressBar><ProgressFill $pct={pct} /></ProgressBar>
          </WizardHeader>

          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ m: '24px 32px 0', borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY, fontSize: TYPE.body }}>
              {error}
            </Alert>
          )}

          {/* ── Step 0: Details ── */}
          {step === 0 && (
            <WizardBody key="s0">
              <SectionLabel>Candidate Information</SectionLabel>
              <InfoGrid>
                {infoItems.map(it => (
                  <InfoCard key={it.label}>
                    <InfoLabel>{it.label}</InfoLabel>
                    <InfoValue>{it.value || '—'}</InfoValue>
                  </InfoCard>
                ))}
              </InfoGrid>

              <SectionLabel style={{ marginTop: 24 }}>Confirm Candidate Email</SectionLabel>
              <EmailInput
                type="email"
                placeholder="candidate@email.com"
                value={candidateEmail}
                onChange={e => setCandidateEmail(e.target.value)}
                required
              />

              <BtnRow>
                <BtnNext type="button" onClick={handleNext} disabled={!emailValid}>
                  Next: Skill Ratings
                  <ArrowBackIosNewIcon sx={{ fontSize: 13, transform: 'rotate(180deg)' }} />
                </BtnNext>
              </BtnRow>
            </WizardBody>
          )}

          {/* ── Step 1: Ratings ── */}
          {step === 1 && (
            <WizardBody key="s1">
              <SectionLabel>Rate Each Skill (0 – 10)</SectionLabel>
              {skills.map(sk => (
                <SkillBlock key={sk.label}>
                  <SkillName>{sk.label}</SkillName>
                  <Slider
                    value={sk.value ?? 0}
                    onChange={(_, v) => sk.set(Math.min(10, Math.max(0, Math.round(Number(v) || 0))))}
                    valueLabelDisplay="auto"
                    step={1} marks min={0} max={10}
                    sx={sliderSx}
                  />
                  <SkillBadge $v={sk.value ?? 0}>{isValidRating(sk.value) ? sk.value : '—'}/10</SkillBadge>
                </SkillBlock>
              ))}

              <SectionLabel style={{ marginTop: 24 }}>Overall Performance</SectionLabel>
              <StyledFormControl>
                <InputLabel>Select overall performance</InputLabel>
                <Select
                  value={reviewRating}
                  onChange={e => setReviewRating(e.target.value)}
                  label="Select overall performance"
                  required
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: `${RADIUS.md}px`,
                        boxShadow: '0 8px 32px rgba(79,55,139,0.15)',
                        '& .MuiMenuItem-root': { fontFamily: FONT_BODY, fontSize: TYPE.body },
                        '& .MuiMenuItem-root:hover': { background: 'rgba(232,222,248,0.6)' },
                      }
                    }
                  }}
                >
                  {overallFeedback.map(r => (
                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                  ))}
                </Select>
              </StyledFormControl>

              <SectionLabel>Written Feedback</SectionLabel>
              <FeedbackTextarea
                placeholder="Share detailed observations about the candidate's performance, strengths, and areas for improvement..."
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                maxLength={FEEDBACK_MAX}
                required
              />
              <Typography sx={{
                fontFamily: FONT_BODY, fontSize: TYPE.caption, lineHeight: 1.5, mt: 1, mb: 1, textAlign: 'right',
                color: feedbackLen > 0 && feedbackLen < FEEDBACK_MIN ? '#B3261E' : '#8B7DB5',
              }}>
                {feedbackLen}/{FEEDBACK_MAX} (min {FEEDBACK_MIN})
              </Typography>
              {!allRated && (
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: COLORS.warning }}>
                  Move every slider to set a rating (0 is allowed).
                </Typography>
              )}

              <BtnRow>
                <BtnBack type="button" onClick={() => { setError(''); setStep(0); }}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
                  Back
                </BtnBack>
                <BtnNext type="button" onClick={handleNext} disabled={!!validateStep(1)}>
                  Next: Review &amp; Submit
                  <ArrowBackIosNewIcon sx={{ fontSize: 13, transform: 'rotate(180deg)' }} />
                </BtnNext>
              </BtnRow>
            </WizardBody>
          )}

          {/* ── Step 2: Review ── */}
          {step === 2 && (
            <WizardBody key="s2">
              <SectionLabel>Review Your Evaluation</SectionLabel>

              <ReviewSection>
                <ReviewTitle>Candidate Details</ReviewTitle>
                {infoItems.map(it => (
                  <ReviewRow key={it.label}>
                    <ReviewKey>{it.label}</ReviewKey>
                    <ReviewVal>{it.value || '—'}</ReviewVal>
                  </ReviewRow>
                ))}
              </ReviewSection>

              <ScoreGrid>
                {skills.map(sk => (
                  <ScoreBox key={sk.label} $bg={getBg(sk.value)} $bdr={getBdr(sk.value)}>
                    <ScoreNum $clr={getClr(sk.value)}>{sk.value}</ScoreNum>
                    <ScoreLbl>{sk.label.replace(' Skills', '')}</ScoreLbl>
                  </ScoreBox>
                ))}
              </ScoreGrid>

              <AvgBand>
                <Box>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Average Score
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: '#5C4A8A', mt: 0.5 }}>
                    Overall: <strong style={{ color: '#2D1B5E' }}>
                      {overallFeedback.find(o => o.value === reviewRating)?.label || reviewRating}
                    </strong>
                  </Typography>
                </Box>
                <Typography sx={{
                  fontFamily: FONT_BODY, fontSize: TYPE.stat, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25,
                  color: getClr(Math.round(avg)),
                  background: getBg(Math.round(avg)),
                  padding: '4px 16px', borderRadius: `${RADIUS.md}px`,
                }}>
                  {avg.toFixed(1)}
                </Typography>
              </AvgBand>

              <ReviewSection>
                <ReviewTitle>Written Feedback</ReviewTitle>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: COLORS.ink, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {feedback}
                </Typography>
              </ReviewSection>

              <BtnRow>
                <BtnBack type="button" onClick={() => { setError(''); setStep(1); }} disabled={loading || submitted}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 13 }} />
                  Back
                </BtnBack>
                <BtnNext type="button" onClick={handleSubmit} disabled={loading || submitted}>
                  {loading ? 'Submitting…' : submitted ? 'Submitted' : 'Submit Evaluation'}
                  <CheckIcon sx={{ fontSize: 15 }} />
                </BtnNext>
              </BtnRow>
            </WizardBody>
          )}
        </WizardCard>
       </Column>

        {/* Success dialog */}
        <Dialog
          open={success}
          onClose={finishAfterSuccess}
          PaperProps={{
            sx: {
              borderRadius: `${RADIUS.lg}px`, padding: '8px',
              boxShadow: '0 20px 60px rgba(79,55,139,0.18)',
              maxWidth: 360, width: '100%',
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: 'linear-gradient(135deg, #E6F4EA, #C8E6C9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <CheckCircleOutlineIcon sx={{ color: '#2D7D46', fontSize: 32 }} />
            </div>
            <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section, letterSpacing: '-0.02em', color: '#2D1B5E', mb: 1 }}>
              Feedback Submitted!
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: '#5C4A8A', lineHeight: 1.5 }}>
              Your evaluation has been recorded. Redirecting to dashboard…
            </Typography>
            <div style={{ marginTop: 24, height: 4, borderRadius: 5, background: '#EDE7F6', overflow: 'hidden' }}>
              <div style={{
                height: '100%', background: 'linear-gradient(90deg, #6750A4, #4F378B)',
                borderRadius: 5, animation: `progress ${SUCCESS_REDIRECT_MS / 1000}s linear forwards`,
              }} />
            </div>
          </DialogContent>
        </Dialog>
        <style>{`@keyframes progress{from{width:0%}to{width:100%}}`}</style>
      </PageWrap>
    </ThemeProvider>
  );
};

export default FeedbackForm;
